import { Action, Selector, State, StateContext } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import {
    ClearSelectedWorkflowConfig,
    LoadSelectedWorkflowConfig,
    LoadWorkflowConfigById,
    LoadWorkflowConfigs
} from './workflow-config.actions';
import { WorkflowConfigService } from '../../services/workflow-config.service';
import { Observable, of, tap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize } from 'rxjs/operators';
import { WorkflowConfig } from '@monorepo/interfaces';

export interface WorkflowConfigStateModel {
    configs: WorkflowConfig[];
    configsCache: WorkflowConfig[];
    selectedWorkflowConfig: WorkflowConfig;
    isLoading: boolean;
    activeRequests: number;
}

@State<WorkflowConfigStateModel>({
    name: 'workflowConfig',
    defaults: {
        configs: [],
        configsCache: [],
        selectedWorkflowConfig: null,
        isLoading: false,
        activeRequests: 0
    }
})
@Injectable()
export class WorkflowConfigState {
    private workflowService = inject(WorkflowConfigService);

    @Selector()
    static configs(state: WorkflowConfigStateModel): WorkflowConfig[] {
        return state.configs;
    }
    @Selector()
    static configsCache(state: WorkflowConfigStateModel): WorkflowConfig[] {
        return state.configsCache;
    }

    @Selector()
    static selectedWorkflowConfig(state: WorkflowConfigStateModel): WorkflowConfig {
        return state.selectedWorkflowConfig;
    }

    @Selector()
    static isLoading(state: WorkflowConfigStateModel): boolean {
        return state.isLoading;
    }

    @Action(LoadWorkflowConfigs)
    loadWorkflowConfigs(
        ctx: StateContext<WorkflowConfigStateModel>,
        action: LoadWorkflowConfigs
    ): Observable<WorkflowConfig[] | HttpErrorResponse> {
        this.incrementActiveRequests(ctx);
        return this.workflowService.getWorkflowConfigs(action.workflowType, action.filter).pipe(
            tap(configs => ctx.patchState({ configs, configsCache: configs })),
            finalize(() => this.decrementActiveRequests(ctx))
        );
    }

    @Action(LoadSelectedWorkflowConfig)
    loadWorkflowConfig(
        ctx: StateContext<WorkflowConfigStateModel>,
        action: LoadSelectedWorkflowConfig
    ): Observable<WorkflowConfig | HttpErrorResponse> {
        const state = ctx.getState();
        const existingConfig = state.configs.find(config => config.id === action.configId);

        if (existingConfig) {
            ctx.patchState({ selectedWorkflowConfig: existingConfig });
            return of(existingConfig);
        }

        this.incrementActiveRequests(ctx);
        return this.workflowService.getWorkflowConfig(action.workflowType, action.configId).pipe(
            tap(selectedWorkflowConfig => {
                ctx.patchState({
                    selectedWorkflowConfig,
                    configsCache: [...ctx.getState().configsCache, selectedWorkflowConfig]
                });
            }),
            finalize(() => this.decrementActiveRequests(ctx))
        );
    }

    @Action(LoadWorkflowConfigById)
    loadWorkflowConfigById(
        ctx: StateContext<WorkflowConfigStateModel>,
        action: LoadWorkflowConfigById
    ): Observable<WorkflowConfig | HttpErrorResponse> {
        const state = ctx.getState();
        const existingConfig = state.configs.find(config => config.id === action.configId);
        if (existingConfig) {
            return of(existingConfig);
        }
        this.incrementActiveRequests(ctx);
        return this.workflowService.getWorkflowConfig(action.workflowType, action.configId).pipe(
            tap(loadedWorkflowConfig => {
                ctx.patchState({
                    configsCache: [...ctx.getState().configsCache, loadedWorkflowConfig]
                });
            }),
            finalize(() => this.decrementActiveRequests(ctx))
        );
    }

    @Action(ClearSelectedWorkflowConfig)
    clearSelectedWorkflowConfig(ctx: StateContext<WorkflowConfigStateModel>): void {
        ctx.patchState({ selectedWorkflowConfig: undefined });
    }

    private incrementActiveRequests(ctx: StateContext<WorkflowConfigStateModel>): void {
        const state = ctx.getState();
        const activeRequests = state.activeRequests + 1;
        ctx.patchState({
            activeRequests,
            isLoading: true
        });
    }

    private decrementActiveRequests(ctx: StateContext<WorkflowConfigStateModel>): void {
        const state = ctx.getState();
        const activeRequests = state.activeRequests - 1;
        ctx.patchState({
            activeRequests,
            isLoading: activeRequests > 0 // Only set to false, if there are no more open requests
        });
    }
}
